import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { StoreContext } from "../../../../../../store/StoreProvider";
import useLocaleRoute from "../../../../../../common/hooks/useLocaleRoute";
import useModulePublic from "../../../../../hooks/useModulePublic";
import IconPopup from "../../../../../../common/components/popup/IconPopup";
import InputPlusSelect from "../../../../../../common/components/input/InputPlusSelect";
import useDebounce from "../../../../../../common/hooks/useDebounce";
import UserListItem from "./UserListItem";
import useModuleLimit from "../../../../../hooks/useModuleLimit";
// config, utils
import urls from "../../../../../../api/project/ApiUrls";
import { MODULE_ID } from "../../../../../../config/CONSTANTS";
import role from "../../../../../../config/App-role";
import { validateEmail } from "../../../../../../common/utils/validator";
import { selectOpts } from "./option";
import fireDb from "../../../../../../api/firebase/realtime/utils";
import Act from "../../../../../../store/actions";
import fireAuth from "../../../../../../api/firebase/auth/utils";
import { checkUserInGroup } from "../../../../service/userGroupService";

const popupMenuPosition = {
  anchorOrigin: {
    vertical: "bottom",
    horizontal: "left",
  },
  transformOrigin: {
    vertical: "top",
    horizontal: "left",
  },
};

const AccordionSummaryTail = () => {
  // props

  // route,intl
  const intl = useIntl();
  const navigate = useNavigate();
  // store
  const [state, dispatch] = useContext(StoreContext);
  const { user } = state;
  const { locale, group } = user;
  // local state
  const [popupPageIdx, setPopupPageIdx] = useState(0);
  const [popPageCtx, setPopPageCtx] = useState({});
  const [emailInput, setEmailInput] = useState("");
  const searchEmailInputDebx = useDebounce(emailInput, 1000);
  // const [roleSelect, setRoleSelect] = useState(role.reader);
  const [roleSelect, setRoleSelect] = useState(role.reader);
  // hooks
  const { handleLocaleRoute } = useLocaleRoute(
    locale,
    urls?.locale_lang?.LOCALE_DEFAULT
  );
  const { handlePublic } = useModulePublic();
  const { handleLimit } = useModuleLimit();

  const disabledIconBtn = !(
    handlePublic(MODULE_ID.snaSidebarGroupAddmember) &&
    fireAuth.isGroupAdmin(group)
  );

  useEffect(() => {
    const isEmail = validateEmail(searchEmailInputDebx);

    // 使用者已在群組中
    const handleUserInGroup = () => {
      setPopPageCtx((prev) => ({
        ...prev,
        footer: [
          {
            children: (
              <Box mb={1} ml={2} mt={2}>
                <Typography variant="body2">使用者已在群組中</Typography>
              </Box>
            ),
            useMenuItem: false,
          },
        ],
      }));
    };

    // 有 match 到使用者
    const handleMatchUser = (_users) => {
      setPopPageCtx((prev) => ({
        ...prev,
        footer: Object.values(_users).map((us, idx) => ({
          children: (
            <Box mb={1} key={idx.toString()}>
              <UserListItem
                key={idx.toString()}
                user={us.info}
                role={roleSelect}
              />
            </Box>
          ),
          useMenuItem: false,
        })),
      }));
    };

    // 沒有 match 到使用者, 但是 email 符合驗證
    const handleEmailValidate = () => {
      const curUser = {
        email: searchEmailInputDebx,
      };
      setPopPageCtx((prev) => ({
        ...prev,
        onClose: () => {
          setEmailInput("");
          setRoleSelect(role.reader);
        },
        footer: [
          {
            children: (
              <Box mb={1}>
                <UserListItem user={curUser} role={roleSelect} />
              </Box>
            ),
            useMenuItem: false,
          },
        ],
      }));
    };

    // 未滿足前二者
    const handleFallback = () => {
      setPopPageCtx((prev) => ({
        ...prev,
        footer: null,
      }));
    };
    // 搜尋使用者
    checkUserInGroup({ email: searchEmailInputDebx, groupId: group.uid })
      .then(({ error, success, userInfo, userInGroup }) => {
        if (userInGroup) {
          // user already in group
          handleUserInGroup();
        } else if (userInfo) {
          // match user(already signup)
          handleMatchUser([{ info: userInfo }]);
        } else if (isEmail) {
          handleEmailValidate();
        } else {
          handleFallback();
        }
      })
      .catch((error) => {
        // error message
        dispatch({
          type: Act.COMMON_SET_ERROR_MESSAGE,
          payload: {
            open: true,
            content: error.message,
          },
        });
        if (isEmail) {
          handleEmailValidate();
        } else {
          handleFallback();
        }
      });
    // // 搜尋使用者
    // // 搜尋 firebase
    // fireDb.findUsersByEmail({
    //   email: searchEmailInputDebx,
    //   onSuccess: (users) => {
    //     // console.log("[onSuccess]users", users);
    //     if (Object.keys(users).length > 0) {
    //       handleMatchUser(users);
    //     } else if (isEmail) {
    //       handleEmailValidate();
    //     } else {
    //       handleFallback();
    //     }
    //   },
    //   onError: (error) => {
    //     // error message
    //     dispatch({
    //       type: Act.COMMON_SET_ERROR_MESSAGE,
    //       payload: {
    //         open: true,
    //         content: error.message,
    //       },
    //     });
    //     if (isEmail) {
    //       handleEmailValidate();
    //     } else {
    //       handleFallback();
    //     }
    //   },
    // });
  }, [searchEmailInputDebx, roleSelect]);

  // popup menu 允許多頁
  const popupPages = [
    {
      // body: popup 主體
      body: [
        {
          children: (
            <Box mt={2} mb={4} px={2}>
              <Typography variant="body2">
                {intl.formatMessage({
                  id: "sna.sidebar.grouptool.invite.member",
                  defaultMessage: "邀請成員",
                })}
              </Typography>
            </Box>
          ),
          useMenuItem: false,
        },
        {
          children: (
            <Box px={2} pb={2}>
              <Box mb={2}>
                <Typography variant="body2">
                  {intl.formatMessage({
                    id: "sna.sidebar.grouptool.email.input.label",
                    defaultMessage: "請輸入電子郵件帳號邀請",
                  })}
                </Typography>
              </Box>
              <InputPlusSelect
                inputProps={{
                  onChange: (e) => {
                    const { value } = e.target;
                    setEmailInput(value);
                  },
                  placeholder: intl.formatMessage({
                    id: "sna.sidebar.grouptool.email.input.placeholder",
                    defaultMessage: "電子郵件",
                  }),
                }}
                selectProps={{
                  value: roleSelect,
                  defaultValue: "",
                  options: selectOpts.map((so) => ({
                    value: so.value,
                    label: so.label,
                  })),
                  inputProps: {},
                  MenuProps: {},
                  onChange: (e) => {
                    setRoleSelect(e.target.value);
                  },
                }}
              />
            </Box>
          ),
          useMenuItem: false,
        },
      ],
      // 依據使用者輸入的 email 字串搜尋相關使用者
      footer: null,
    },
  ];

  useEffect(() => {
    setPopPageCtx(popupPages[popupPageIdx]);
  }, [popupPageIdx]);

  const getIconButtonProps = () => ({
    onButtonIconClick: () => null,
    sx: {
      color: "#fff",
      "&.Mui-disabled": {
        color: "#909090",
      },
    },
    disabled: disabledIconBtn,
  });

  const getMenuProps = () => ({
    sx: {
      minWidth: "272px",
    },
    ...popPageCtx,
    ...popupMenuPosition,
  });

  return (
    <IconPopup
      icon={<AddIcon />}
      iconButtonProps={getIconButtonProps()}
      menuProps={getMenuProps()}
    />
  );
};

export default AccordionSummaryTail;
